import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import { Field } from "@/pages/FridgeLogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, MapPin, Users, Tag, ListChecks, Receipt, Paperclip, Upload, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Checklist from "@/components/Checklist";
import { formatMoney } from "@/lib/currency";

function EventAttachments({ eventId }) {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/attachments`);
      setAttachments(data);
    } catch {
      setAttachments([]);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { if (eventId) load(); }, [eventId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File exceeds 20 MB limit");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/events/${eventId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: att.content_type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename || "attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const del = async (att) => {
    if (!window.confirm(`Delete "${att.original_filename}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      toast.success("Attachment removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const formatSize = (b) => {
    if (!b) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="border-t border-[#e5e2d9] pt-4" data-testid="event-attachments">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Attachments</div>
          <div className="text-[11px] text-[#8a8476] mt-0.5">BEOs, public liability certs, contracts, menus. Max 20MB each.</div>
        </div>
        <label className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#1b3b36] cursor-pointer hover:underline">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? "Uploading…" : "Upload file"}
          <input
            type="file"
            data-testid="event-attachment-upload"
            className="hidden"
            disabled={uploading}
            onChange={handleUpload}
          />
        </label>
      </div>
      <div className="border border-[#e5e2d9] divide-y divide-[#efece6]" data-testid="event-attachments-list">
        {loaded && attachments.length === 0 && (
          <div className="py-6 text-center text-xs text-[#8a8476]">No attachments yet.</div>
        )}
        {attachments.map((att, idx) => (
          <div key={att.id} data-testid={`event-attachment-row-${idx}`} className="px-3 py-2.5 flex items-center gap-3 hover:bg-[#efece6]/40">
            <Paperclip className="w-3.5 h-3.5 text-[#8a8476] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{att.original_filename}</div>
              <div className="text-[10px] text-[#8a8476] uppercase tracking-[0.14em] font-mono">
                {formatSize(att.size)} · {att.uploaded_by}
              </div>
            </div>
            <button
              type="button"
              data-testid={`event-attachment-download-${idx}`}
              onClick={() => download(att)}
              className="text-[#8a8476] hover:text-[#1b3b36]"
              aria-label="Download"
              title="Download"
            >
              <FileDown className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid={`event-attachment-delete-${idx}`}
              onClick={() => del(att)}
              className="text-[#8a8476] hover:text-[#d94f3d]"
              aria-label="Delete"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const EVENT_TYPES = [
  { value: "catering", label: "Catering" },
  { value: "private_hire", label: "Private hire" },
  { value: "tasting", label: "Tasting / Supper club" },
  { value: "popup", label: "Pop-up" },
  { value: "market", label: "Market / Trader" },
  { value: "delivery", label: "Delivery / Wholesale" },
  { value: "other", label: "Other" },
];

const TYPE_LABELS = EVENT_TYPES.reduce((a, t) => ((a[t.value] = t.label), a), {});

const SHOWS_GUEST_COUNT = new Set(["catering", "private_hire", "tasting", "popup"]);
const SHOWS_PITCH = new Set(["market", "delivery"]);
const SHOWS_DIETARY = new Set(["catering", "private_hire", "tasting"]);

const VENUE_LABELS = {
  catering: "Venue",
  private_hire: "Venue",
  tasting: "Venue",
  popup: "Location",
  market: "Market name",
  delivery: "Drop-off address",
  other: "Location",
};

const empty = {
  title: "",
  venue: "",
  event_date: new Date().toISOString().slice(0, 10),
  event_type: "catering",
  guest_count: 0,
  pitch_info: "",
  dietary_notes: "",
  checklist: [],
  invoice_id: null,
  status: "planned",
  notes: "",
};

export default function Events() {
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterType, setFilterType] = useState("all");

  const load = async () => setItems((await api.get("/events")).data);
  const loadInvoices = async () => {
    try { setInvoices((await api.get("/invoices")).data); } catch { setInvoices([]); }
  };
  useEffect(() => { load(); loadInvoices(); }, []);

  const invoicesById = useMemo(
    () => invoices.reduce((a, i) => ((a[i.id] = i), a), {}),
    [invoices]
  );

  const openCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      title: r.title,
      venue: r.venue,
      event_date: r.event_date,
      event_type: r.event_type || "catering",
      guest_count: r.guest_count || 0,
      pitch_info: r.pitch_info || "",
      dietary_notes: r.dietary_notes || "",
      checklist: r.checklist || [],
      invoice_id: r.invoice_id || null,
      status: r.status,
      notes: r.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const showsGuests = SHOWS_GUEST_COUNT.has(form.event_type);
      const body = {
        ...form,
        guest_count: showsGuests ? Number(form.guest_count) : 0,
        pitch_info: SHOWS_PITCH.has(form.event_type) ? form.pitch_info : "",
        dietary_notes: SHOWS_DIETARY.has(form.event_type) ? form.dietary_notes : "",
        checklist: form.checklist.filter((c) => (c.text || "").trim()),
      };
      if (editingId) {
        await api.put(`/events/${editingId}`, body);
        toast.success("Event updated");
      } else {
        await api.post("/events", body);
        toast.success("Event scheduled");
      }
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const loadTemplate = async () => {
    try {
      const { data } = await api.get(`/events/checklist-template/${form.event_type}`);
      // Merge: keep existing items, append template items that don't already exist.
      const existingTexts = new Set(form.checklist.map((c) => (c.text || "").trim().toLowerCase()));
      const additions = (data.items || []).filter((t) => !existingTexts.has(t.text.trim().toLowerCase()));
      setForm((f) => ({ ...f, checklist: [...f.checklist, ...additions] }));
      if (additions.length === 0) toast.message("Template already loaded");
      else toast.success(`Added ${additions.length} template item${additions.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Couldn't load template");
    }
  };

  const del = async (id) => { await api.delete(`/events/${id}`); toast.success("Event removed"); load(); };

  const eventDates = useMemo(() => items.map((e) => new Date(e.event_date)), [items]);

  const filtered = useMemo(() => {
    const sorted = items.slice().sort((a, b) => a.event_date.localeCompare(b.event_date));
    if (filterType === "all") return sorted;
    return sorted.filter((e) => (e.event_type || "catering") === filterType);
  }, [items, filterType]);

  const showsGuests = SHOWS_GUEST_COUNT.has(form.event_type);
  const showsPitch = SHOWS_PITCH.has(form.event_type);
  const showsDietary = SHOWS_DIETARY.has(form.event_type);

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-[1500px]" data-testid="events-page">
      <PageHeader
        eyebrow="Bookings & service"
        title="Event schedule"
        description="Plan dates, venues and headcount across every booking — pop-ups, markets, deliveries, tastings and catering jobs."
        actions={
          <Button data-testid="event-add-button" onClick={openCreate} className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] h-10 px-5">
            <Plus className="w-4 h-4 mr-2" /> New event
          </Button>
        }
      />

      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-1 mb-6 border-b border-[#e5e2d9] pb-1" data-testid="event-filter-bar">
        {[{ value: "all", label: "All" }, ...EVENT_TYPES].map((t) => {
          const count = t.value === "all"
            ? items.length
            : items.filter((i) => (i.event_type || "catering") === t.value).length;
          return (
            <button
              key={t.value}
              data-testid={`event-filter-${t.value}`}
              onClick={() => setFilterType(t.value)}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] transition-colors border-b-2 -mb-[1px] ${
                filterType === t.value
                  ? "border-[#1b3b36] text-[#1b3b36]"
                  : "border-transparent text-[#8a8476] hover:text-[#1c1917]"
              }`}
            >
              {t.label}
              <span className="ml-2 font-mono text-[10px] text-[#8a8476]">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#e5e2d9] p-6" data-testid="event-calendar">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476] mb-3">Calendar</div>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => d && setSelectedDate(d)}
            modifiers={{ booked: eventDates }}
            modifiersClassNames={{ booked: "bg-[#e5efe8] text-[#1b3b36] font-medium" }}
            className="p-0"
          />
          <div className="mt-4 text-xs text-[#8a8476] flex items-center gap-2">
            <span className="w-3 h-3 bg-[#e5efe8] inline-block" /> dates with events
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-[#e5e2d9]" data-testid="event-list">
          <div className="px-6 py-4 border-b border-[#e5e2d9] flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Upcoming & past</div>
            <div className="text-xs text-[#57534e]">{filtered.length} events</div>
          </div>
          {filtered.length === 0 && (
            <div className="py-16 text-center text-sm text-[#8a8476]">
              {filterType === "all" ? "No events scheduled. Add your first booking." : `No ${TYPE_LABELS[filterType]} events.`}
            </div>
          )}
          {filtered.map((e, idx) => {
            const t = e.event_type || "catering";
            const showG = SHOWS_GUEST_COUNT.has(t);
            const showP = SHOWS_PITCH.has(t);
            return (
              <div key={e.id} data-testid={`event-row-${idx}`} className="px-6 py-5 border-b border-[#efece6] last:border-0 flex items-start gap-5 hover:bg-[#efece6]/30">
                <div className="text-center min-w-[58px]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a8476]">{new Date(e.event_date).toLocaleDateString(undefined, { month: "short" })}</div>
                  <div className="font-display text-3xl mt-0.5 text-[#1b3b36]">{new Date(e.event_date).getDate()}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-[15px]">{e.title}</h3>
                    <StatusPill tone="neutral"><Tag className="w-3 h-3" /> {TYPE_LABELS[t]}</StatusPill>
                    <StatusPill tone={e.status === "confirmed" ? "success" : e.status === "cancelled" ? "alert" : e.status === "completed" ? "neutral" : "warning"}>{e.status}</StatusPill>
                  </div>
                  <div className="mt-1.5 text-xs text-[#57534e] flex items-center gap-4 flex-wrap">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.venue}</span>
                    {showG && <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {e.guest_count} guests</span>}
                    {showP && e.pitch_info && <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" /> {e.pitch_info}</span>}
                  </div>
                  {e.dietary_notes && SHOWS_DIETARY.has(t) && (
                    <p className="text-xs text-[#57534e] mt-2"><span className="text-[#8a8476]">Dietary:</span> {e.dietary_notes}</p>
                  )}
                  {e.notes && <p className="text-xs text-[#8a8476] mt-1 line-clamp-1">{e.notes}</p>}
                  {Array.isArray(e.checklist) && e.checklist.length > 0 && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#8a8476]" data-testid={`event-checklist-progress-${idx}`}>
                      <ListChecks className="w-3 h-3" />
                      <span className="font-mono text-[#1b3b36]">{e.checklist.filter((c) => c.done).length} / {e.checklist.length}</span>
                      <span>kit ready</span>
                    </div>
                  )}
                  {e.invoice_id && invoicesById[e.invoice_id] && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#1b3b36]" data-testid={`event-invoice-link-${idx}`}>
                      <Receipt className="w-3 h-3" />
                      <span className="font-mono">{invoicesById[e.invoice_id].invoice_number}</span>
                      <span className="text-[#8a8476]">•</span>
                      <span className="font-mono">{formatMoney(invoicesById[e.invoice_id].total, invoicesById[e.invoice_id].currency || "GBP")}</span>
                      <StatusPill tone={invoicesById[e.invoice_id].status === "paid" ? "success" : invoicesById[e.invoice_id].status === "overdue" ? "alert" : "warning"}>
                        {invoicesById[e.invoice_id].status}
                      </StatusPill>
                    </div>
                  )}
                </div>
                <div className="inline-flex items-center gap-3">
                  <button data-testid={`event-edit-${idx}`} onClick={() => openEdit(e)} className="text-[#8a8476] hover:text-[#1b3b36]" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`event-delete-${idx}`} onClick={() => del(e.id)} className="text-[#8a8476] hover:text-[#d94f3d]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }}>
        <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit event" : "Schedule event"}</DialogTitle></DialogHeader>
          <form className="space-y-4" onSubmit={submit} data-testid="event-form">
            <Field label="Event type">
              <Select value={form.event_type} onValueChange={(v) => setForm((f) => ({ ...f, event_type: v }))}>
                <SelectTrigger data-testid="event-type-select" className="bg-white rounded-[4px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Event name"><Input required data-testid="event-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={VENUE_LABELS[form.event_type] || "Venue"}>
                <Input required data-testid="event-venue-input" value={form.venue} onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))} className="bg-white rounded-[4px]" />
              </Field>
              <Field label="Date"><Input type="date" required data-testid="event-date-input" value={form.event_date} onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
            </div>

            {showsGuests && (
              <Field label="Guests / covers" data-testid="event-guests-field">
                <Input type="number" min="0" data-testid="event-guests-input" value={form.guest_count} onChange={(e) => setForm((f) => ({ ...f, guest_count: e.target.value }))} className="bg-white rounded-[4px]" />
              </Field>
            )}
            {showsPitch && (
              <Field label={form.event_type === "delivery" ? "Order / route info" : "Pitch / stall info"}>
                <Input data-testid="event-pitch-input" value={form.pitch_info} onChange={(e) => setForm((f) => ({ ...f, pitch_info: e.target.value }))} className="bg-white rounded-[4px]" placeholder={form.event_type === "delivery" ? "PO #, route, contact…" : "Pitch number, stall size, fees…"} />
              </Field>
            )}
            {showsDietary && (
              <Field label="Dietary requirements">
                <Textarea data-testid="event-dietary-input" value={form.dietary_notes} onChange={(e) => setForm((f) => ({ ...f, dietary_notes: e.target.value }))} className="bg-white rounded-[4px]" placeholder="Allergens, vegan, gluten-free…" />
              </Field>
            )}

            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="event-status-select" className="bg-white rounded-[4px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="bg-white rounded-[4px]" /></Field>

            <Field label="Link to invoice (optional)">
              <Select
                value={form.invoice_id || "__none__"}
                onValueChange={(v) => setForm((f) => ({ ...f, invoice_id: v === "__none__" ? null : v }))}
              >
                <SelectTrigger data-testid="event-invoice-select" className="bg-white rounded-[4px]">
                  <SelectValue placeholder="No invoice linked" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__" data-testid="event-invoice-option-none">
                    <span className="text-[#8a8476] italic">No invoice linked</span>
                  </SelectItem>
                  {invoices.map((inv) => (
                    <SelectItem key={inv.id} value={inv.id} data-testid={`event-invoice-option-${inv.id}`}>
                      <span className="font-mono mr-2">{inv.invoice_number}</span>
                      <span className="text-[#57534e]">{inv.client_name}</span>
                      <span className="text-[#8a8476] ml-2 font-mono">{formatMoney(inv.total, inv.currency || "GBP")}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {invoices.length === 0 && (
                <div className="text-[10px] text-[#8a8476] italic mt-1">No invoices yet — create one in the Invoices tab.</div>
              )}
            </Field>

            <div className="border-t border-[#e5e2d9] pt-4">
              <Checklist
                value={form.checklist}
                onChange={(next) => setForm((f) => ({ ...f, checklist: next }))}
                onLoadTemplate={loadTemplate}
              />
            </div>

            {editingId && <EventAttachments eventId={editingId} />}

            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="event-submit-button" className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]">
                {busy ? "Saving…" : editingId ? "Save changes" : "Save event"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
